import {
  cleanInputCreate,
  cleanInputUpdate,
  CreateInvoiceSettingInput,
  CreateInvoiceSettingMutationVariables,
  createInvoiceSetting as createMutation,
  CustomSubscription,
  CustomSubscriptionType,
  DateTime,
  DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
  getDateTimeDifference,
  getDefaultInvoiceSettingValues,
  getInvoiceSetting as getQuery,
  getReadId,
  getTableClientId,
  getTypedCustomSubscriptionValueForRefetch,
  INVOICE_DOCUMENT_TYPES,
  INVOICE_SETTING_DOCUMENT_FIELDS,
  INVOICE_SETTING_TABLE_NAME,
  INVOICE_TABLE_NAME,
  InvoiceDocumentType,
  InvoiceSetting,
  onCreateCustomSubscription,
  OnCreateCustomSubscriptionSubscription,
  onDeleteCustomSubscription,
  OnDeleteCustomSubscriptionSubscription,
  onUpdateCustomSubscription,
  OnUpdateCustomSubscriptionSubscription,
  removeNestedFieldsExcept,
  syncInvoiceSettings,
  todayDateTime,
  UpdateInvoiceSettingInput,
  updateInvoiceSetting as updateMutation,
} from '@rentguru/commons-utils';
import {
  list,
  mutation,
  mutationWithFetchBefore,
  NUMBER_OF_MINUTES_FOR_REFETCH,
  useSubscriptions,
} from '@up2rent/graphql';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { createContext, ReactNode, Reducer, useContext, useEffect, useMemo, useReducer } from 'react';

import { useUser } from './UserContext';

export interface InvoiceDocumentFormatUpdate {
  format: string;
  incrementNumber?: number;
}

export interface InvoiceSettingContextState extends State {
  getInvoiceSettingForOwner: (ownerId: string) => InvoiceSetting | undefined;
  ensureInvoiceSettingForOwner: (ownerId: string) => Promise<InvoiceSetting>;
  updateInvoiceFormat: (ownerId: string, invoiceFormat: string) => Promise<InvoiceSetting>;
  updateInvoiceDocumentFormats: (
    ownerId: string,
    updates: Partial<Record<InvoiceDocumentType, InvoiceDocumentFormatUpdate>>
  ) => Promise<InvoiceSetting>;
  updateCustomInvoicingConfiguration: (ownerId: string, enabled: boolean) => Promise<InvoiceSetting>;
  updateDefaultVatRate: (ownerId: string, vatRateId: string) => Promise<InvoiceSetting>;
  updateFiscalYearStart: (ownerId: string, fiscalYearStart: string | null) => Promise<InvoiceSetting>;
  updateAllPurposeInvoiceIssuer: (ownerId: string, enabled: boolean) => Promise<InvoiceSetting>;
  updatePaymentBankAccount: (ownerId: string, bankAccountId: string | null) => Promise<InvoiceSetting>;
}

interface State {
  loading: boolean;
  shouldFetch: boolean;
  lastInvoiceSettingsFetch: DateTime | null;
  invoiceSettings: InvoiceSetting[];
}

type Action =
  | {
      type: 'FETCH_INVOICE_SETTINGS' | 'REFETCH_INVOICE_SETTINGS';
    }
  | {
      type: 'FETCH_FAILED';
    }
  | {
      type: 'FETCHED';
      payload: { invoiceSettings: InvoiceSetting[] };
    }
  | {
      type: 'ADD' | 'UPDATE';
      payload: { invoiceSetting: InvoiceSetting };
    };

const initialState: State = {
  loading: false,
  shouldFetch: true,
  invoiceSettings: [],
  lastInvoiceSettingsFetch: null,
};

const contextReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'FETCH_INVOICE_SETTINGS':
      if (
        state.loading ||
        state.shouldFetch ||
        (!isNil(state.lastInvoiceSettingsFetch) &&
          getDateTimeDifference({ start: state.lastInvoiceSettingsFetch, end: todayDateTime() }, 'minutes') <
            NUMBER_OF_MINUTES_FOR_REFETCH)
      ) {
        return state;
      }
      return {
        ...state,
        shouldFetch: true,
        loading: true,
      };
    case 'REFETCH_INVOICE_SETTINGS':
      return {
        ...state,
        shouldFetch: true,
        loading: true,
      };
    case 'FETCHED':
      return {
        ...state,
        loading: false,
        shouldFetch: false,
        lastInvoiceSettingsFetch: todayDateTime(),
        invoiceSettings: [
          ...action.payload.invoiceSettings,
          ...state.invoiceSettings.filter(
            (oldInvoiceSetting) =>
              !action.payload.invoiceSettings.some((newInvoiceSetting) => newInvoiceSetting.id === oldInvoiceSetting.id)
          ),
        ],
      };
    case 'FETCH_FAILED':
      return { ...state, loading: false, shouldFetch: false };
    case 'ADD':
      return {
        ...state,
        invoiceSettings: [...state.invoiceSettings, action.payload.invoiceSetting],
      };
    case 'UPDATE':
      return {
        ...state,
        invoiceSettings: state.invoiceSettings.map((item) =>
          item.id === action.payload.invoiceSetting.id ? action.payload.invoiceSetting : item
        ),
      };
    default:
      return state;
  }
};

export const InvoiceSettingContext = createContext<InvoiceSettingContextState | null>(null);

interface InvoiceSettingContextProviderProps {
  children?: ReactNode;
}

const INVOICE_SETTING_RELATIONS_TO_REMOVE = ['owner'];

const INVOICE_SETTINGS_QUERY = removeNestedFieldsExcept(
  syncInvoiceSettings,
  DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
  INVOICE_SETTING_RELATIONS_TO_REMOVE
);

export const InvoiceSettingContextProvider = ({ children }: InvoiceSettingContextProviderProps) => {
  const { clientId } = useUser();
  const [state, dispatch] = useReducer<Reducer<State, Action>>(contextReducer, initialState);

  const fetchInvoiceSettings = async (): Promise<InvoiceSetting[]> =>
    await list<InvoiceSetting>(
      INVOICE_SETTINGS_QUERY,
      'clientId',
      getTableClientId(clientId!, INVOICE_SETTING_TABLE_NAME)
    );

  useEffect(() => {
    let unmounted = false;
    const fetchAndSet = async () => {
      try {
        const invoiceSettings = await fetchInvoiceSettings();
        if (!unmounted) {
          dispatch({ type: 'FETCHED', payload: { invoiceSettings } });
        }
      } catch (error) {
        console.error('[invoiceSettings] fetching invoice settings failed', error);
        if (!unmounted) {
          dispatch({ type: 'FETCH_FAILED' });
        }
      }
    };

    if (state.shouldFetch) {
      fetchAndSet();
    }
    return () => {
      unmounted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetch]);

  useSubscriptions<
    OnCreateCustomSubscriptionSubscription,
    OnUpdateCustomSubscriptionSubscription,
    OnDeleteCustomSubscriptionSubscription
  >(
    onCreateCustomSubscription,
    onUpdateCustomSubscription,
    onDeleteCustomSubscription,
    (data) => {
      const newData = data.onCreateCustomSubscription as CustomSubscription;
      if (
        newData.name === CustomSubscriptionType.REFETCH &&
        getTypedCustomSubscriptionValueForRefetch(newData).table === INVOICE_TABLE_NAME
      ) {
        dispatch({ type: 'REFETCH_INVOICE_SETTINGS' });
      }
    },
    () => {},
    () => {}
  );

  const getInvoiceSettingForOwner = (ownerId: string): InvoiceSetting | undefined =>
    state.invoiceSettings.find((invoiceSetting) => invoiceSetting.ownerId === ownerId);

  const createInvoiceSettingForOwner = async (ownerId: string): Promise<InvoiceSetting> => {
    const input: CreateInvoiceSettingInput = {
      ...getDefaultInvoiceSettingValues(),
      ownerId,
      readId: getReadId(clientId!, INVOICE_SETTING_TABLE_NAME),
      clientId: getTableClientId(clientId!, INVOICE_SETTING_TABLE_NAME),
    };
    const invoiceSetting = await mutation<InvoiceSetting, CreateInvoiceSettingMutationVariables>(createMutation, {
      input: cleanInputCreate(input) as CreateInvoiceSettingInput,
    });
    dispatch({ type: 'ADD', payload: { invoiceSetting } });
    return invoiceSetting;
  };

  const ensureInvoiceSettingForOwner = async (ownerId: string): Promise<InvoiceSetting> => {
    const knownInvoiceSetting = getInvoiceSettingForOwner(ownerId);
    if (!isNil(knownInvoiceSetting)) {
      return knownInvoiceSetting;
    }

    const invoiceSettings = await fetchInvoiceSettings();
    if (!isEmpty(invoiceSettings)) {
      dispatch({ type: 'FETCHED', payload: { invoiceSettings } });
    }
    const existingInvoiceSetting = invoiceSettings.find((invoiceSetting) => invoiceSetting.ownerId === ownerId);
    if (!isNil(existingInvoiceSetting)) {
      return existingInvoiceSetting;
    }

    return await createInvoiceSettingForOwner(ownerId);
  };

  const updateInvoiceDocumentFormats = async (
    ownerId: string,
    updates: Partial<Record<InvoiceDocumentType, InvoiceDocumentFormatUpdate>>
  ): Promise<InvoiceSetting> => {
    const original = await ensureInvoiceSettingForOwner(ownerId);
    const now = todayDateTime().toString();
    const changedFields = INVOICE_DOCUMENT_TYPES.reduce<Partial<Record<keyof InvoiceSetting, string | number>>>(
      (acc, documentType) => {
        const update = updates[documentType];
        if (isNil(update)) {
          return acc;
        }
        const fields = INVOICE_SETTING_DOCUMENT_FIELDS[documentType];
        acc[fields.format] = update.format;
        if (!isNil(update.incrementNumber)) {
          acc[fields.incrementNumber] = update.incrementNumber;
          acc[fields.lastUpdate] = now;
        }
        return acc;
      },
      {}
    );

    const invoiceSetting = await mutationWithFetchBefore<InvoiceSetting>(original.id, getQuery, updateMutation, {
      input: {
        ...cleanInputUpdate({ id: original.id, _version: original._version, ...changedFields }, false),
      } as UpdateInvoiceSettingInput,
    });
    dispatch({ type: 'UPDATE', payload: { invoiceSetting } });
    return invoiceSetting;
  };

  const updateInvoiceFormat = async (ownerId: string, invoiceFormat: string): Promise<InvoiceSetting> =>
    await updateInvoiceDocumentFormats(ownerId, { [InvoiceDocumentType.INVOICE]: { format: invoiceFormat } });

  const updateInvoiceSettingFields = async (
    ownerId: string,
    fields: Partial<
      Pick<
        InvoiceSetting,
        | 'allPurposeInvoiceIssuer'
        | 'customInvoicingConfiguration'
        | 'defaultVatRateId'
        | 'fiscalYearStart'
        | 'paymentBankAccountId'
      >
    >
  ): Promise<InvoiceSetting> => {
    const original = await ensureInvoiceSettingForOwner(ownerId);
    const invoiceSetting = await mutationWithFetchBefore<InvoiceSetting>(original.id, getQuery, updateMutation, {
      input: {
        ...cleanInputUpdate({ id: original.id, _version: original._version, ...fields }, false),
      } as UpdateInvoiceSettingInput,
    });
    dispatch({ type: 'UPDATE', payload: { invoiceSetting } });
    return invoiceSetting;
  };

  const updateCustomInvoicingConfiguration = async (ownerId: string, enabled: boolean): Promise<InvoiceSetting> =>
    await updateInvoiceSettingFields(ownerId, { customInvoicingConfiguration: enabled });

  const updateDefaultVatRate = async (ownerId: string, vatRateId: string): Promise<InvoiceSetting> =>
    await updateInvoiceSettingFields(ownerId, { defaultVatRateId: vatRateId });

  const updateFiscalYearStart = async (ownerId: string, fiscalYearStart: string | null): Promise<InvoiceSetting> =>
    await updateInvoiceSettingFields(ownerId, { fiscalYearStart });

  const updateAllPurposeInvoiceIssuer = async (ownerId: string, enabled: boolean): Promise<InvoiceSetting> =>
    await updateInvoiceSettingFields(ownerId, { allPurposeInvoiceIssuer: enabled });

  const updatePaymentBankAccount = async (ownerId: string, bankAccountId: string | null): Promise<InvoiceSetting> =>
    await updateInvoiceSettingFields(ownerId, { paymentBankAccountId: bankAccountId });

  const values = useMemo(
    () => ({
      ...state,
      getInvoiceSettingForOwner,
      ensureInvoiceSettingForOwner,
      updateInvoiceFormat,
      updateInvoiceDocumentFormats,
      updateCustomInvoicingConfiguration,
      updateDefaultVatRate,
      updateFiscalYearStart,
      updateAllPurposeInvoiceIssuer,
      updatePaymentBankAccount,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return <InvoiceSettingContext.Provider value={values}>{children}</InvoiceSettingContext.Provider>;
};

export const useInvoiceSettings = (): InvoiceSettingContextState => {
  const context = useContext<InvoiceSettingContextState | null>(InvoiceSettingContext);

  if (!context) {
    throw new Error('`useInvoiceSettings` hook must be used within a `InvoiceSettingContextProvider` component');
  }
  return context;
};
